import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import styles from './index.css';

const useModalOpen = (keepPosition = true) => {
    useEffect(() => {
        if (document.body.style.position !== 'fixed') {
            const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;

            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
            if (keepPosition) {
                document.body.style.top = `${-scrollTop}px`;
            }

            return () => {
                document.body.style.position = 'unset';
                document.body.style.width = 'unset';
                document.body.style.overflow = '';
                if (keepPosition) {
                    document.body.scrollTop = document.documentElement.scrollTop = scrollTop;
                }
            };
        }
    }, []);
};

const Wrap = ({ children, top, onClose, show, dragToClose }) => {
    useModalOpen();

    const touchStartPositionRef = useRef(0);
    const touchMoveDiffRef = useRef(0);
    const touchMovingRef = useRef(false);
    const scrolledRef = useRef(false);
    const diffLimitRef = useRef(0);

    const contentRef = useRef();
    const scrollRef = useRef();

    const handleTouchStart = e => {
        // 触摸开始事件，仅考虑单点触摸
        const { targetTouches } = e;
        const touch = targetTouches[0];
        // 触摸点基于屏幕的y轴坐标
        const { screenY } = touch;

        // 触摸开始时使用ref将最开始的触摸点基于屏幕的y轴坐标记录下来
        touchStartPositionRef.current = screenY;
        // 将表示正在进行触摸事件的ref设置为true
        touchMovingRef.current = true;
        // 取消回弹动画
        contentRef.current.style.transition = 'unset';

        const scrollTop = scrollRef.current.scrollTop;

        scrolledRef.current = scrollTop > 0;

        if (!diffLimitRef.current) {
            const winWidth = Math.min(window.innerWidth, window.__IFENG_ENV__.maxDocWidth);

            diffLimitRef.current = Math.floor((278 * winWidth) / 750);
        }
    };

    const handleTouchMove = e => {
        const scrollTop = scrollRef.current.scrollTop;

        if (scrollTop > 0) {
            scrolledRef.current = true;
        }

        if (scrolledRef.current) {
            return;
        }
        // 触摸移动事件
        if (touchMovingRef.current) {
            // 仅正在进行触摸事件的ref设置为true时继续执行（当进入关闭浮层阶段时为false）
            const { targetTouches } = e;
            const touch = targetTouches[0];
            // 触摸点基于屏幕的y轴坐标
            const { screenY } = touch;
            // 当前触摸点基于屏幕的y轴坐标和初始触摸点基于屏幕的y轴坐标之差
            const diff = screenY - touchStartPositionRef.current;

            if (diff > 0) {
                // 如果上一步的差大于0，证明是向下拖拽，此时才继续执行
                // 将内容节点的overflow设置为hidden是为了安卓性能问题，否则向上滚动至顶部然后向下拖拽动画十分卡顿
                scrollRef.current.style.overflowY = 'hidden';
                // 设置拖拽距离
                contentRef.current.style.transform = `translate3d(0px, ${diff}px, 0px)`;
                touchMoveDiffRef.current = diff;

                if (diff >= diffLimitRef.current) {
                    // 如果向下拖拽的距离大于等于限制距离，关闭浮层
                    onClose();
                    // 将表示正在进行触摸事件的ref设置为false，防止阻碍之后的关闭动画
                    touchMovingRef.current = false;
                }
            }
        }
    };

    const handleTouchEnd = () => {
        // 触摸结束事件
        if (touchMovingRef.current) {
            // 正在进行触摸事件的ref为true，表示此时是进行了触摸开始事件和触摸移动事件后，进入改触摸结束事件
            // 如果进入该事件，证明浮层并没有关闭（浮层关闭时不触发此事件）
            // 需要进行以下重置操作：

            // 将内容节点的overflow设置为scroll
            scrollRef.current.style.overflowY = 'scroll';
            // 将表示正在进行触摸事件的ref为false
            touchMovingRef.current = false;
            if (touchMoveDiffRef.current < diffLimitRef.current) {
                // 如果向下拖拽的距离小于限制距离，进入重置阶段
                // 设置回弹transition（仅在这个时刻设置transition是由于拖拽时本身就是连续的操作，如果加上transition动画反而会导致拖拽卡顿）
                contentRef.current.style.transition = 'transform 0.3s ease';
                // 设置回弹至初始位置
                // setTranslateY(0);
                contentRef.current.style.transform = 'translate3d(0px, 0px, 0px)';
                // 将触摸开始的y轴坐标和触摸移动距离重置为0（其实不重置也可以）
                touchStartPositionRef.current = 0;
                touchMoveDiffRef.current = 0;
            }
        }
    };

    return (
        <div className={styles.wrap}>
            <div
                className={`${styles.mask} ${show ? styles.maskShow : styles.maskHide}`}
                onClick={e => {
                    e.stopPropagation();
                    onClose();
                }}
            />
            <div
                className={`${styles.content} ${show ? styles.contentShow : styles.contentHide}`}
                style={{ top }}
                {...(dragToClose
                    ? {
                          onTouchStart: handleTouchStart,
                          onTouchMove: handleTouchMove,
                          onTouchEnd: handleTouchEnd,
                      }
                    : {})}
                ref={contentRef}>
                <div className={styles.scroll} ref={scrollRef}>
                    {children}
                </div>
            </div>
        </div>
    );
};

Wrap.propTypes = {
    children: PropTypes.any,
    top: PropTypes.any,
    onClose: PropTypes.func,
    show: PropTypes.bool,
    dragToClose: PropTypes.bool,
};

const Drag = ({ show, children, top, onClose, dragToClose = true }) => {
    const [root, setRoot] = useState(null);
    const [showState, setShowState] = useState(show);

    useEffect(() => {
        const root = document.createElement('div');

        document.body.append(root);

        setRoot(root);
    }, []);

    useEffect(
        () => {
            if (show) {
                setShowState(show);
            } else {
                setTimeout(() => {
                    setShowState(show);
                }, 300);
            }
        },
        [show],
    );

    return showState && root
        ? ReactDOM.createPortal(
              <Wrap top={top} onClose={onClose} show={show} dragToClose={dragToClose}>
                  {children}
              </Wrap>,
              root,
          )
        : null;
};

export default Drag;
